import { AdobeAnalyticsTrackingService } from "../tracking/adobe-analytics-tracking.service";

enum SearchTrackingActions {
  Select = "select",
  Type = "type",
  Return = "return",
  SelectWidget = "select-suggestion-widget",
  DisplayWidget = "display-suggestion-widget",
}

enum SearchTrackingCategories {
  LiveSearch = "live-search",
}

export class SearchTrackingService {
  private static instance: SearchTrackingService;
  private adobeAnalyticsTrackingService: AdobeAnalyticsTrackingService;

  static getInstance(): SearchTrackingService {
    if (!SearchTrackingService.instance) {
      SearchTrackingService.instance = new SearchTrackingService();
    }
    return SearchTrackingService.instance;
  }

  private constructor() {
    // Private constructor -> singleton
    this.adobeAnalyticsTrackingService =
      AdobeAnalyticsTrackingService.getInstance();
  }

  /**
   * Used to track the selection of a suggestion
   *
   * @param searchTerm string
   * @param selectedSuggestion string
   * @param suggestionPosition number
   * @param location string
   */
  trackSuggestionSelection(
    searchTerm: string,
    selectedSuggestion: string,
    suggestionPosition: number,
    location: string,
  ): void {
    this.adobeAnalyticsTrackingService.track(
      {
        location,
        label: `${searchTerm}_${selectedSuggestion}_${suggestionPosition}`,
        action: SearchTrackingActions.Select,
        category: SearchTrackingCategories.LiveSearch,
      },
      {
        search: {
          events: ["searchResultEntryClick"],
          term: searchTerm,
        },
      },
    );
  }

  /**
   * Used to track a suggestion search
   *
   * @param searchTerm string
   * @param location string
   */
  trackSuggestionSearch(searchTerm: string, location: string) {
    this.adobeAnalyticsTrackingService.track({
      location,
      label: searchTerm,
      action: SearchTrackingActions.Type,
      category: SearchTrackingCategories.LiveSearch,
    });
  }

  /**
   * Used to track if the user executes a search
   *
   * @param searchTerm string
   * @param location string
   */
  trackSearch(searchTerm: string, location: string) {
    this.adobeAnalyticsTrackingService.track({
      location,
      label: searchTerm,
      action: SearchTrackingActions.Return,
      category: SearchTrackingCategories.LiveSearch,
    });
  }

  /**
   * Used to track selection within a widget (e.g. click on a link)
   *
   * @param searchTerm string
   * @param widgetName string
   * @param title string title of the selected element
   * @param location string
   */
  trackWidgetSelection(
    searchTerm: string,
    widgetName: string,
    title: string,
    location: string,
  ): void {
    this.adobeAnalyticsTrackingService.track({
      location,
      label: `${searchTerm}_${widgetName}_${title}`,
      action: SearchTrackingActions.SelectWidget,
      category: SearchTrackingCategories.LiveSearch,
    });
  }

  /**
   * Used to track when a widget has been displayed
   *
   * @param searchTerm string
   * @param widgetName string
   * @param location string
   */
  trackWidgetDisplayed(
    searchTerm: string,
    widgetName: string,
    location: string,
    results: number,
  ) {
    const events = ["searchQuery"];
    if (!results) {
      events.push("noResults");
    }
    this.adobeAnalyticsTrackingService.track(
      {
        location,
        label: `${searchTerm}_${widgetName}`,
        action: SearchTrackingActions.DisplayWidget,
        category: SearchTrackingCategories.LiveSearch,
      },
      {
        search: {
          events,
          term: searchTerm,
          results,
        },
      },
    );
  }
}
